import {
  PHONE_NUMBER_REGEX,
  MAX_FILE_STR_LEN,
  GrantType,
  ApplicationStatusText,
  ApplicationStatusTextKeys,
  PaymentStatusKeys,
  EvenAppPaymentSubTypeKeys,
  PaymentStatus,
  EvenAppPaymentSubType,
} from './constants';
import appConfig from './appConfig.json'

export const grantDashboardBreadcrumbsData = [
  {
    name: 'ACNT Homepage',
    path: '/',
  },
  {
    name: 'My Grant Dashboard',
    active: true,
    path: '',
  },
];

export const fileToBase64 = async (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (e) => reject(e);
  });

export const withHttps = (url: string) => (!/^https?:\/\//i.test(url) ? `https://${url}` : url);

export const phoneNumberFormat = (phoneNumber) => {
  let match;
  let phoneNumberFormatted;
  if (phoneNumber?.length === 10) {
    match = phoneNumber?.match(PHONE_NUMBER_REGEX);
  }
  if (match) {
    phoneNumberFormatted = `${match[1]} ${match[2]} ${match[3]}`;
    return phoneNumberFormatted;
  }
  return phoneNumber;
};

export const formatAmount = (value: number) =>
  value?.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) || 0;

export const longTextFormatter = (value: string, maxLength = MAX_FILE_STR_LEN) => {
  const firstPart = value.substring(0, maxLength / 2);
  const secondPart = value.substring(value?.length - maxLength / 2, value?.length);
  if (value?.length > maxLength) {
    return `${firstPart}...${secondPart}`;
  }
  return value;
};

export const prefixApplicationIdWithGrantType = (grantType, applicationId) => {
  const prefix = grantType === GrantType.DISASTER ? 'D' : 'A';
  return prefix + applicationId;
};

export const cleanupPrefixFromApplicationId = (applicationId) => {
  if (typeof applicationId === 'string') {
    return applicationId.replace(/^[A-Za-z]+|[A-Za-z]+|^[A-Za-z]+$/g, '');
  }
  return applicationId;
};

export const onlyNumbers = /^\d*$/;

interface FetchCCMResponse {
  featureFlags: {
    [key: string]: any;
  };
  commentReasonsForHardship: string[];
  rejectionReasons: string[];
  stateTerritoryAbbreviations: {
    [countryCode: string]: {
      [stateCode: string]: any;
    };
  };
  documentTypes: string[];
  enabledLanguages: string[];
  editAddressLink: string;
  cohabitationFileTypeIds: string[];
}

export const fetchCCM = async (): Promise<FetchCCMResponse> => {
  try {
    let data;
    if (window.location.hostname === 'localhost') {
      data = appConfig.configResolution.resolved;
    } else {
      const response = await fetch('/api/ccm');
      if (!response.ok) {
        throw new Error(`HTTP error, status: ${response.status}`);
      }
      const result = await response.json();
      data = JSON.parse(atob(result.data));
    }
    const { commentReasonsForHardship, rejectionReasons, stateTerritoryAbbreviations,
      documentTypes, enabledLanguages, editAddressLink, cohabitationFileTypeIds, ...featureFlags } = data;
    return {
      featureFlags,
      commentReasonsForHardship: commentReasonsForHardship,
      rejectionReasons: rejectionReasons,
      stateTerritoryAbbreviations: JSON.parse(stateTerritoryAbbreviations),
      documentTypes: documentTypes,
      enabledLanguages: enabledLanguages || ['en'],
      editAddressLink: editAddressLink,
      cohabitationFileTypeIds: cohabitationFileTypeIds || [],
    };
  } catch (error) {
    console.error('Error fetching CCM:', error);
    throw error;
  }
};

// i18n helper: get translated application status text
export const getApplicationStatusText = (
  status: string,
  t: (key: string) => string
): string => {
  const key = ApplicationStatusTextKeys[status];
  return key ? t(key) : ApplicationStatusText[status] || status;
};

// i18n helper: get status filter options for dropdowns
export const getStatusFilterOptions = (
  t: (key: string) => string
): { label: string; value: string }[] => {
  return Object.entries(ApplicationStatusTextKeys).map(([value, key]) => ({
    label: t(key),
    value,
  }));
};

// i18n helper: get translated payment status text
export const getPaymentStatusText = (
  status: string,
  t: (key: string) => string
): string => {
  const key = PaymentStatusKeys[status];
  return key ? t(key) : PaymentStatus[status as keyof typeof PaymentStatus] || status;
};

// i18n helper: get translated payment sub type text
export const getPaymentSubTypeText = (
  subType: string,
  t: (key: string) => string
): string => {
  const key = EvenAppPaymentSubTypeKeys[subType];
  return key ? t(key) : EvenAppPaymentSubType[subType as keyof typeof EvenAppPaymentSubType] || subType;
};
